/*
 * Marketplace specific configuration.
 *
 * Every filter needs to have following keys:
 * - id:     Unique id of the filter.
 * - label:  The default label of the filter.
 * - type:   String that represents one of the existing filter components:
 *           BookingDateRangeFilter, KeywordFilter, PriceFilter,
 *           SelectSingleFilter, SelectMultipleFilter.
 * - group:  Is this 'primary' or 'secondary' filter?
 *           Primary filters are visible on desktop layout by default.
 *           Secondary filters are behind "More filters" button.
 *           Read more from src/containers/SearchPage/README.md
 * - queryParamNames: Describes parameters to be used with queries
 *                    (e.g. 'price' or 'pub_amenities'). Most of these are
 *                    the same between webapp URLs and API query params.
 *                    You can't change 'dates', 'price', or 'keywords'
 *                    since those filters are fixed to a specific attribute.
 * - config: Extra configuration that the filter component needs.
 *
 * Note 1: Labels could be tied to translation file
 *         by importing FormattedMessage:
 *         <FormattedMessage id="some.translation.key.here" />
 *
 * Note 2: If you need to add new custom filter components,
 *         you need to take those into use in:
 *         src/containers/SearchPage/FilterComponent.js
 *
 * Note 3: If you just want to create more enum filters
 *         (i.e. SelectSingleFilter, SelectMultipleFilter),
 *         you can just add more configurations with those filter types
 *         and tie them with correct extended data key
 *         (i.e. pub_<key> or meta_<key>).
 */
import React from 'react';
import { FormattedMessage } from 'react-intl';

export const filters = [
  {
    id: 'dates-length',
    label: 'Dates',
    type: 'BookingDateRangeLengthFilter',
    group: 'primary',
    // Note: BookingDateRangeFilter is fixed filter,
    // you can't change "queryParamNames: ['dates'],"
    queryParamNames: ['dates', 'minDuration'],
    config: {
      // A global time zone to use in availability searches. As listings
      // can be in various time zones, we must decide what time zone we
      // use in search when looking for available listings within a
      // certain time interval.
      //
      // If you have all/most listings in a certain time zone, change this
      // config value to that.
      //
      // See: https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
      searchTimeZone: 'Etc/UTC',

      // Options for the minimum duration of the booking
      options: [
        { key: '0', label: 'Any length' },
        { key: '60', label: '1 hour', shortLabel: '1h' },
        { key: '120', label: '2 hours', shortLabel: '2h' },
      ],
    },
  },
  {
    id: 'price',
    label: 'Price',
    type: 'PriceFilter',
    group: 'primary',
    // Note: PriceFilter is fixed filter,
    // you can't change "queryParamNames: ['price'],"
    queryParamNames: ['price'],
    // Price filter configuration
    // Note: unlike most prices this is not handled in subunits
    config: {
      min: 0,
      max: 300,
      step: 5,
    },
  },
  {
    id: 'keyword',
    label: 'Keyword',
    type: 'KeywordFilter',
    group: 'primary',
    // Note: KeywordFilter is fixed filter,
    // you can't change "queryParamNames: ['keywords'],"
    queryParamNames: ['keywords'],
    // NOTE: If you are ordering search results by distance
    // the keyword search can't be used at the same time.
    // You can turn on/off ordering by distance from config.js file.
    config: {},
  },
  {
    id: 'tourlyCategories',
    label: <FormattedMessage id="EditListingFeaturesCategory.Label" />,
    type: 'SelectMultipleFilter',
    group: 'secondary',
    queryParamNames: ['pub_tourlyCategories'],
    config: {
      // Optional modes: 'has_all', 'has_any'
      // https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
      searchMode: 'has_any',

      // "key" is the option you see in Flex Console.
      // "label" is set here for this web app's UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [
        {
          key: 'roadexcursions',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.roadexcursions" />,
        },
        {
          key: 'boattours',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.boattours" />,
        },
        {
          key: 'hiking',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.hiking" />,
        },
        {
          key: 'transfers',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.transfers" />,
        },
        {
          key: 'offroad',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.offroad" />,
        },
        {
          key: 'canyoning',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.canyoning" />,
        },
        { key: 'btt', label: <FormattedMessage id="EditListingFeaturesCategory.Label.btt" /> },
        {
          key: 'biggamefishing',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.biggamefishing" />,
        },
        {
          key: 'gardensreserves',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.gardensreserves" />,
        },
        {
          key: 'kayaking',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.kayaking" />,
        },
        {
          key: 'scubasnorkeling',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.scubasnorkeling" />,
        },
        {
          key: 'culturemuseums',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.culturemuseums" />,
        },
        {
          key: 'sightseeing',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.sightseeing" />,
        },
        {
          key: 'paragliding',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.paragliding" />,
        },
        {
          key: 'fitness',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.fitness" />,
        },
        {
          key: 'surfbodyboard',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.surfbodyboard" />,
        },
        {
          key: 'wellbeing',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.wellbeing" />,
        },
        { key: 'wine', label: <FormattedMessage id="EditListingFeaturesCategory.Label.wine" /> },
        {
          key: 'gastronomydrinks',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.gastronomydrinks" />,
        },
        {
          key: 'craftsworkshops',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.craftsworkshops" />,
        },
        {
          key: 'seasonaltours',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.seasonaltours" />,
        },
        {
          key: 'accessibility',
          label: <FormattedMessage id="EditListingFeaturesCategory.Label.accessibility" />,
        },
      ],
    },
  },
  {
    id: 'certificate',
    label: 'Offers pick-up/drop-off',
    type: 'SelectSingleFilter',
    group: 'secondary',
    queryParamNames: ['pub_certificate'],
    config: {
      // "key" is the option you see in Flex Console.
      // "label" is set here for the UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [
        { key: 'no', label: 'No', hideFromFilters: true, hideFromListingInfo: true },
        { key: 'yes', label: 'Yes' },
        // { key: '500h', label: 'Registered yoga teacher 500h' },
      ],
    },
  },
  {
    id: 'duration',
    label: <FormattedMessage id="EditListingDescriptionForm.durationLabel" />,
    type: 'SelectSingleFilter',
    group: 'secondary',
    queryParamNames: ['pub_duration'],
    config: {
      options: [
        {
          key: '1orless',
          label: <FormattedMessage id="EditListingDescriptionForm.duration.1orless" />,
          labelMsgId: 'EditListingDescriptionForm.duration.1orless',
        },
        {
          key: '1to2',
          label: <FormattedMessage id="EditListingDescriptionForm.duration.1to2" />,
          labelMsgId: 'EditListingDescriptionForm.duration.1to2',
        },
        {
          key: '3to4',
          label: <FormattedMessage id="EditListingDescriptionForm.duration.3to4" />,
          labelMsgId: 'EditListingDescriptionForm.duration.3to4',
        },
        {
          key: '5to6',
          label: <FormattedMessage id="EditListingDescriptionForm.duration.5to6" />,
          labelMsgId: 'EditListingDescriptionForm.duration.5to6',
        },
        {
          key: '7to8',
          label: <FormattedMessage id="EditListingDescriptionForm.duration.7to8" />,
          labelMsgId: 'EditListingDescriptionForm.duration.7to8',
        },
        {
          key: '8ormore',
          label: <FormattedMessage id="EditListingDescriptionForm.duration.8ormore" />,
          labelMsgId: 'EditListingDescriptionForm.duration.8ormore',
        },
        {
          key: 'multiday',
          label: <FormattedMessage id="EditListingDescriptionForm.duration.multiday" />,
          labelMsgId: 'EditListingDescriptionForm.duration.multiday',
        },
      ],
    },
  },
];

export const sortConfig = {
  // Enable/disable the sorting control in the SearchPage
  active: true,

  // Note: queryParamName 'sort' is fixed,
  // you can't change it since Flex API expects it to be named as 'sort'
  queryParamName: 'sort',

  // Internal key for the relevance option, see notes below.
  relevanceKey: 'relevance',

  // Keyword filter is sorting the results already by relevance.
  // If keyword filter is active, we need to disable sorting.
  conflictingFilters: ['keyword'],

  options: [
    { key: 'createdAt', label: 'Newest' },
    { key: '-createdAt', label: 'Oldest' },
    { key: '-price', label: 'Lowest price' },
    { key: 'price', label: 'Highest price' },

    // The relevance is only used for keyword search, but the
    // parameter isn't sent to the Marketplace API. The key is purely
    // for handling the internal state of the sorting dropdown.
    { key: 'relevance', label: 'Relevance', longLabel: 'Relevance (Keyword search)' },
  ],
};
